import React, { useState, useEffect, MouseEvent } from 'react';
import TopNav from './TopNav/TopNav';
import SideNav from './SideNav/SideNav';
import { IS_SIDE_NAVIGATION_OPEN, PAGE_INFO } from 'src/util/constants';
import { FmtpPage } from './types';
import { FmtpError, FmtpErrorCode, Permissions, Report } from '@lanterds/types';
import { navigationEnabled } from 'src/util/featureFlags';
import { useKeycloak } from '@react-keycloak/web';
import { isAuthorized, isAuthorizedToViewSomeReports } from 'src/util/Permissions';
import { PageNames } from 'src/util/types';
import { SideNavContextProvider } from 'src/components/common/NavigationMenu/SideNav/SideNavContext';
import { useReportsContext } from '../../Views/Reports/ReportsContext';
import { getCapitalized } from 'src/util/helpers';

export interface NavigationMenuProps {
    selectedNavItem?: string;
    drawerWidth?: number;
}

export default function NavigationMenu({ selectedNavItem, drawerWidth }: NavigationMenuProps) {
    const isSessionDrawerOpen = sessionStorage.getItem(IS_SIDE_NAVIGATION_OPEN) ?? true;
    const [isDrawerOpen, setIsDrawerOpen] = useState(isSessionDrawerOpen === 'true');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const { reports } = useReportsContext();
    const handleClick = (event: MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleLogout = () => {
        sessionStorage.clear();
        keycloak.logout();
    };

    const { keycloak } = useKeycloak();
    const { tokenParsed } = keycloak;
    const userDisplayName =
        tokenParsed?.given_name && tokenParsed?.family_name
            ? `${tokenParsed.given_name.charAt(0)}. ${tokenParsed.family_name}`
            : tokenParsed?.name;
    const userEmail = tokenParsed?.email;

    const toggleDrawer = () => {
        setIsDrawerOpen(!isDrawerOpen);
    };

    useEffect(() => {
        sessionStorage.setItem(IS_SIDE_NAVIGATION_OPEN, String(isDrawerOpen));
    }, [isDrawerOpen]);

    // ALSO ADD NEW PERMISSIONS TO THEIR RELATIVE LAYOUT FILES
    const isParcelSearchAuthorized = isAuthorized(userEmail, Permissions.parcelSearch);
    const isShippingPortalReturnsAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_returns);
    const isShippingPortalTransfersAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_transfers);
    const isShippingPortalOutboundAuthorized = isAuthorized(userEmail, Permissions.shippingPortal_outbound);
    const isUserManagementAuthorized = isAuthorized(userEmail, Permissions.userManagement);
    const isCustomerManagementAuthorized = isAuthorized(userEmail, Permissions.customerManagement);
    const isReportManagementAuthorized = isAuthorized(userEmail, Permissions.reportManagement);
    const isReportsAuthorized = isAuthorizedToViewSomeReports(userEmail);
    const isShipmentTrackerAuthorized = isAuthorized(userEmail, Permissions.shipmentTracking);
    const isDeliveryManagementAuthorized = isAuthorized(userEmail, Permissions.deliveryManagementTool);

    const appNavigationLinks: FmtpPage[] = [];
    const subPageMap: { shippingPortal: PageNames[]; admin: PageNames[]; reports: Report[] | null } = {
        shippingPortal: [],
        admin: [],
        reports,
    };

    // This sets the order of the navigation menu
    if (isParcelSearchAuthorized) appNavigationLinks.push(PAGE_INFO.parcelSearch);
    if (isShipmentTrackerAuthorized) appNavigationLinks.push(PAGE_INFO.shipmentTracker);
    if (isShippingPortalReturnsAuthorized || isShippingPortalTransfersAuthorized || isShippingPortalOutboundAuthorized)
        appNavigationLinks.push(PAGE_INFO.shippingPortal);

    if (isShippingPortalReturnsAuthorized) {
        subPageMap.shippingPortal.push(PageNames.Return);
    }
    if (isShippingPortalTransfersAuthorized) {
        subPageMap.shippingPortal.push(PageNames.Transfer);
    }
    if (isShippingPortalOutboundAuthorized) {
        subPageMap.shippingPortal.push(PageNames.Outbound);
    }

    if (isDeliveryManagementAuthorized) appNavigationLinks.push(PAGE_INFO.deliveryManagement);
    if (isReportsAuthorized) appNavigationLinks.push(PAGE_INFO.reports);

    const adminSubPageNames = [PageNames.Users, PageNames.Customers, PageNames.ReportManagement];

    const adminSubPages = adminSubPageNames.reduce((acc: PageNames[], name) => {
        let isAuthed = false;

        switch (name) {
            case PageNames.Customers:
                if (isCustomerManagementAuthorized) {
                    isAuthed = true;
                }
                break;
            case PageNames.ReportManagement:
                if (isReportManagementAuthorized) {
                    isAuthed = true;
                }
                break;
            case PageNames.Users:
            default:
                if (isUserManagementAuthorized) {
                    isAuthed = true;
                }
                break;
        }

        if (isAuthed) {
            acc.push(name);
        }

        return acc;
    }, []);

    if (adminSubPages.length > 1) {
        appNavigationLinks.push(PAGE_INFO.admin);
        subPageMap.admin = adminSubPages;
    } else if (adminSubPages.length === 1) {
        // User only has a single admin permission, so no sub pages
        const pageName = adminSubPages[0];
        const subPage = PAGE_INFO.admin.subPages?.find((page: FmtpPage) => page.name === pageName);

        if (!subPage) {
            const message = `An error occurred while creating Admin navigation menu item for: ${getCapitalized(pageName)}`;
            const errorCode: FmtpErrorCode = 'SHU021';
            throw new FmtpError({
                message,
                errorCode,
            });
        }

        // Reports admin is a special case where we should still show Admin with a dropdown to reports because
        // we don't want to show 'Reports' in the Navbar twice
        if (pageName === PageNames.ReportManagement) {
            appNavigationLinks.push(PAGE_INFO.admin);
            subPageMap.admin = adminSubPages;
        } else {
            appNavigationLinks.push(subPage);
        }
    }

    return (
        <>
            <TopNav
                selectedNavItem={selectedNavItem}
                navLinks={
                    navigationEnabled ? appNavigationLinks : appNavigationLinks?.filter((link) => link.name === selectedNavItem)
                }
                isDrawerOpen={isDrawerOpen}
                toggleDrawer={toggleDrawer}
                userMenuClickHandler={handleClick}
                userDisplayName={userDisplayName}
                anchorElement={anchorEl}
                menuCloseHandler={handleClose}
                logoutHandler={handleLogout}
                subPageMap={subPageMap}
            />
            <SideNavContextProvider>
                <SideNav
                    navLinks={
                        navigationEnabled
                            ? appNavigationLinks
                            : appNavigationLinks?.filter((link) => link.name === selectedNavItem)
                    }
                    toggleDrawer={toggleDrawer}
                    isDrawerOpen={isDrawerOpen}
                    selectedNavItem={selectedNavItem}
                    drawerWidth={drawerWidth}
                    userDisplayName={userDisplayName}
                    logoutHandler={handleLogout}
                />
            </SideNavContextProvider>
        </>
    );
}
